import { apiClient } from './apiClient';

/** One row in the history list — aggregate counts only. */
export interface KeywordResearchHistoryItem {
  id: string;
  tenant_id: string;
  source: 'standalone_preview' | 'campaign_run';
  company_information_preview: string;
  pages_count: number;
  cluster_count: number;
  total_keywords: number;
  selected_keyword_count?: number;
  used_cluster_count: number;
  not_used_cluster_count: number;
  expires_at: string | null;
  created_at: string;
  selection_saved_at?: string | null;
  activity_at: string;
}

export interface KeywordResearchHistoryResponse {
  runs: KeywordResearchHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

/** One cluster in the detail response, with usage info. */
export interface KeywordResearchHistoryClusterDetail {
  cluster_name: string;
  landing_page: string | null;
  keywords: Array<{
    keyword: string;
    cpc?: number;
    volume?: number;
    selected_for_campaign?: boolean | null;
  }>;
  used: boolean;
  used_in_campaign_run_id: string | null;
  used_state: string | null;
}

export interface KeywordResearchHistoryDetail {
  id: string;
  tenant_id: string;
  source: 'standalone_preview' | 'campaign_run';
  company_information: string;
  pages: string[];
  existing_target_keywords: string[];
  clusters: KeywordResearchHistoryClusterDetail[];
  expires_at: string | null;
  created_at: string;
  selection_saved_at?: string | null;
  activity_at: string;
}

export interface KeywordResearchActiveRunResponse {
  tenant_id: string;
  active_run_id: string | null;
  updated_at: string | null;
  run: KeywordResearchHistoryDetail | null;
  migration_required?: boolean;
  message?: string | null;
}

export interface ListHistoryParams {
  tenant_id: string;
  limit?: number;
  offset?: number;
  source?: 'standalone_preview' | 'campaign_run';
}

const keywordResearchHistoryBaseKey = ['google-ads', 'keyword-research', 'history'] as const;

export const keywordResearchHistoryQueryKeys = {
  all: keywordResearchHistoryBaseKey,
  lists: () => [...keywordResearchHistoryBaseKey, 'list'] as const,
  list: (params: ListHistoryParams) =>
    [
      ...keywordResearchHistoryQueryKeys.lists(),
      params.tenant_id,
      params.limit ?? 20,
      params.offset ?? 0,
      params.source ?? null,
    ] as const,
  detail: (runId: string | null) =>
    [...keywordResearchHistoryBaseKey, 'run', runId] as const,
  active: (tenantId: string) =>
    [...keywordResearchHistoryBaseKey, 'active', tenantId] as const,
};

export const keywordResearchHistoryService = {
  async list(params: ListHistoryParams): Promise<KeywordResearchHistoryResponse> {
    const { data } = await apiClient.get<KeywordResearchHistoryResponse>(
      '/google-ads/keyword-research/history',
      { params },
    );
    return data;
  },

  async get(runId: string): Promise<KeywordResearchHistoryDetail> {
    const { data } = await apiClient.get<KeywordResearchHistoryDetail>(
      `/google-ads/keyword-research/history/${runId}`,
    );
    return data;
  },

  async getActive(tenantId: string): Promise<KeywordResearchActiveRunResponse> {
    const { data } = await apiClient.get<KeywordResearchActiveRunResponse>(
      '/google-ads/keyword-research/active',
      { params: { tenant_id: tenantId } },
    );
    return data;
  },

  async setActive(params: {
    tenant_id: string;
    run_id: string;
  }): Promise<KeywordResearchActiveRunResponse> {
    const { data } = await apiClient.post<KeywordResearchActiveRunResponse>(
      '/google-ads/keyword-research/active',
      params,
    );
    return data;
  },
};
